/**
 * Display formatting.
 *
 * Money goes through `@bingo/shared` — the same `santim()` and `formatEtb()`
 * the server uses. A second implementation on the client is how "500 santim"
 * becomes "5.00" on one screen and "500.00" on another, and money that renders
 * differently in two places is money the player stops trusting.
 */

import { santim, formatEtb, formatEtbWhole, parseEtb, remainderAfterBps } from '@bingo/shared';
import type { Messages } from './i18n/messages.ts';

/**
 * The flat rate every round currently resolves to (migration
 * `015_standard_commission.sql`) — used only as a pre-lock estimate, before
 * `round.commissionBps` is actually resolved and frozen. If the tier table
 * ever grows real bands this stops being exact for the waiting-room figure,
 * same as the existing `entryFee × cards` estimate below it already was.
 */
const ESTIMATED_COMMISSION_BPS = 2200;

/**
 * A wire amount (a decimal string of santim) as ETB.
 *
 * Parsing to bigint first is deliberate: `Number('9007199254740993')` rounds,
 * and a formatter that silently rounds a balance is worse than one that throws.
 */
export function etb(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return `ETB ${formatEtb(santim(amount))}`;
  } catch {
    return '—';
  }
}

/** Same, without the currency prefix — for tight spaces like a card header. */
export function etbBare(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return formatEtb(santim(amount));
  } catch {
    return '—';
  }
}

/**
 * A prize amount, floored to whole birr with no fractional santim shown
 * (e.g. "1744" not "1744.55") — prize figures read as false precision
 * otherwise. Not for balances, deposits, or withdrawals, which keep exact
 * `.XX` display via {@link etb}/{@link etbBare}.
 */
export function etbWhole(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return `ETB ${formatEtbWhole(santim(amount))}`;
  } catch {
    return '—';
  }
}

export type AmountParse =
  | { ok: true; santim: string }
  | { ok: false; reason: 'empty' | 'invalid' };

/**
 * Turns what a player typed ("50", "50.5", "1,200.00") into a santim string.
 *
 * Uses `@bingo/shared`'s `parseEtb`, which is the same function an operator's
 * input goes through server-side — so "12.345" is rejected in one place, by one
 * rule, rather than by a regex here that drifts from the real one later.
 *
 * Thousands separators are stripped first because people type them and a form
 * that rejects "1,200" is a form arguing with its user about punctuation.
 */
export function parseAmount(input: string): AmountParse {
  const cleaned = input.replace(/[\s,]/g, '');
  if (cleaned === '') return { ok: false, reason: 'empty' };
  try {
    return { ok: true, santim: parseEtb(cleaned).toString() };
  } catch {
    return { ok: false, reason: 'invalid' };
  }
}

/** Compares two wire amounts. Both are santim strings, so compare as bigint. */
export function amountGreaterThan(a: string, b: string): boolean {
  return BigInt(a) > BigInt(b);
}

export function amountLessThan(a: string, b: string): boolean {
  return BigInt(a) < BigInt(b);
}

export function isZero(a: string): boolean {
  return BigInt(a) === 0n;
}

/** `mm:ss` remaining until an ISO timestamp, or null once it has passed. */
export function countdown(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null;
  const remaining = new Date(iso).getTime() - now;
  if (remaining <= 0) return null;
  const total = Math.ceil(remaining / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * `hh:mm:ss` remaining until an ISO timestamp, or null once it has passed.
 *
 * For the final day of an Arif Bingo countdown (once `daysUntil` drops to
 * null) — `mm:ss` alone would run past 99 minutes and read as a wall of
 * digits, so this keeps the hour visible down to the last second.
 */
export function countdownHMS(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null;
  const remaining = new Date(iso).getTime() - now;
  if (remaining <= 0) return null;
  const total = Math.ceil(remaining / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/** A short, human-quotable round reference — what a player reads out to support. */
export function roundRef(roundId: string): string {
  return roundId.slice(0, 4).toUpperCase();
}

/**
 * What the prize is, or would be if the round locked right now — **net of
 * commission**, what a winner would actually receive, not the gross pool.
 *
 * `prizePool` is null until the round goes live, because it is only real once
 * the stakes are actually in escrow. Showing a dash until then reads as
 * "nothing to win", so before lock this projects `entryFee × cards` — the same
 * arithmetic the server will do. Shared by the lobby and the round screen so
 * one player cannot see two different prizes for one round.
 *
 * `round.commissionBps` is null until the round locks (the rate is resolved
 * from the tier table at that moment and frozen — see `resolveCommissionBps`
 * server-side), so before lock this estimates with `ESTIMATED_COMMISSION_BPS`
 * rather than showing gross. That estimate stops being exact only if the tier
 * table grows real bands instead of one flat rate; the exact figure replaces
 * it the instant the round starts either way.
 */
export function prizeFigure(round: {
  prizePool: string | null;
  entryFee: string;
  cardCount?: number;
  commissionBps?: number | null;
  fixedPrizeAmount?: string | null;
  gameTypeSlug?: string | null;
}): string {
  // Arif Bingo pays a flat 50,000 ETB jackpot regardless of pool size — see
  // game_rounds.fixed_prize_amount (migration 024). The round opened before
  // that column existed may still carry NULL, so fall back to the constant the
  // game type now declares. Nothing to estimate: this is the number, win or
  // wait.
  if (round.gameTypeSlug === 'arif') {
    return round.fixedPrizeAmount ?? ARIF_JACKPOT_SANTIM;
  }
  if (round.fixedPrizeAmount !== null && round.fixedPrizeAmount !== undefined) {
    return round.fixedPrizeAmount;
  }
  const gross =
    round.prizePool !== null
      ? BigInt(round.prizePool)
      : BigInt(round.entryFee) * BigInt(round.cardCount ?? 0);
  const bps = round.commissionBps ?? ESTIMATED_COMMISSION_BPS;
  return remainderAfterBps(santim(gross), bps).toString();
}

/**
 * Arif Bingo's flat jackpot: 50,000 ETB, as santim. Mirrors the game type's
 * `fixed_prize_amount` (5,000,000) for rounds that opened before migration 024
 * and therefore hold NULL.
 */
export const ARIF_JACKPOT_SANTIM = '5000000';

/**
 * Whole days remaining until an ISO timestamp — the "3 days" face of the Arif
 * countdown. Returns `null` once the timestamp is under a day away (or has
 * passed): inside 24h the display switches to the `mm:ss` clock via
 * {@link countdown}. Whole days are `ceil`ed so the display never shows
 * "0 days" while there is still a day left.
 */
export function daysUntil(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  const remaining = new Date(iso).getTime() - now;
  if (remaining <= 86_400_000) return null;
  return Math.ceil(remaining / 86_400_000);
}

/**
 * The weekday (0=Sunday..6=Saturday) of an ISO timestamp, or null.
 *
 * Used to show *which* day an Arif Bingo round starts on next to the "Starts
 * in X days" countdown — derived from the round's own `starts_at`, so it
 * tracks the schedule by itself (this Saturday's round says Saturday; the
 * next round, whenever it opens, says its own day).
 */
export function weekdayIndex(iso: string | null): number | null {
  if (!iso) return null;
  return new Date(iso).getDay();
}

/**
 * The human-facing "Win by: …" line for a round's winning pattern, or null
 * when the server did not send a pattern name. Known names get their localized
 * description; anything else falls back to showing the raw name rather than
 * hiding that the pattern exists.
 */
export function winPatternLabel(
  name: string | null | undefined,
  t: Messages,
): string | null {
  if (!name) return null;
  const what =
    name === 'full_house'
      ? t.patternFullHouse
      : name === 'single_line'
        ? t.patternSingleLine
        : name === 'four_corners'
          ? t.patternFourCorners
          : name === 'line_or_corners'
            ? t.patternLineOrCorners
            : t.patternUnknown(name);
  return t.winBy(what);
}

/** The B/I/N/G/O letter for a number, from its column range. */
export function ballLetter(n: number): string {
  return ['B', 'I', 'N', 'G', 'O'][Math.floor((n - 1) / 15)] ?? '';
}
